import React, { CSSProperties, useEffect, useState } from 'react'
import SubscriptionList from '../Subscriptions/SubscriptionList'
import Header from './Header/Header'
import { Filters, Subscription, Tenant } from '../../Interfaces/Types'
import { useSubscriptions } from '../Context/SubscriptionContext'
import { useApi } from '../../Hooks'

// The panel is a flex column so the AddActionBar can pin to the bottom while the
// list area in between scrolls independently.
const rootStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
}

const scrollAreaStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
  paddingRight: '6px'
}

const Section1: React.FC = () => {
  const [query, setQuery] = useState('')
  const [filters, setFilters] = useState<Filters>({
    environment: { options: [], selected: [] as string[] },
    deployStatus: { options: [], selected: [] as string[] },
    showSubscriptionsWithTenant: true,
    showSubscriptionsWithoutTenant: false
  })
  const [searchResults, setSearchResults] = useState<{ subscriptions?: Subscription[]; tenants?: Tenant[] }>({})
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const { fetchApi } = useApi()
  const { selectedSubscriptionId, setSelectedSubscriptionId } = useSubscriptions()

  // Typing a search deselects the current subscription so the global search
  // results (matching subscriptions + tenants) take over the panel.
  useEffect(() => {
    if (query && selectedSubscriptionId !== null) {
      setSelectedSubscriptionId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  // Opening a subscription (from results or the list) clears the active search,
  // so selecting a hit doesn't immediately get deselected by the effect above.
  useEffect(() => {
    if (selectedSubscriptionId !== null && query) {
      setQuery('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubscriptionId])

  useEffect(() => {
    if (query || filters.environment.selected.length > 0 || filters.deployStatus.selected.length > 0) {
      setIsSearchLoading(true)
      const environments = filters.environment.selected
      const deployStatuses = filters.deployStatus.selected

      const queryParams = new URLSearchParams({
        query,
        ...(environments.length > 0 && { environment: environments.join(',') }),
        ...(deployStatuses.length > 0 && { deployStatus: deployStatuses.join(',') })
      }).toString()

      fetchApi({
        endpoint: `/api/search/search?${queryParams}`,
        method: 'GET'
      })
        .then((data) => {
          setSearchResults(data)
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          setIsSearchLoading(false)
        })
    } else {
      setSearchResults({})
    }
  }, [query, filters, fetchApi])

  return (
    <div style={rootStyle}>
      <Header query={query} setQuery={setQuery} filters={filters} setFilters={setFilters} />
      <div style={scrollAreaStyle} className='custom-scrollbar'>
        <SubscriptionList
          query={query}
          searchResults={searchResults}
          isSearchLoading={isSearchLoading}
          showSubscriptionsWithTenant={filters.showSubscriptionsWithTenant}
          showSubscriptionsWithoutTenant={filters.showSubscriptionsWithoutTenant}
        />
      </div>
    </div>
  )
}

export default Section1
